import type { Theme } from "@mui/material/styles";
import type { InviteTier } from "../../../api/person/events";
import type { ActivityEntry } from "./timeline-types";

export interface EntryChipProps {
	label: string;
	color?: "success" | "info" | "primary" | "secondary" | "warning" | "error";
	customColor?: string;
	/** Shown on chip hover — e.g. the raw send error for an undeliverable email. */
	tooltip?: string;
}

/**
 * Why the nightly invite job sent this invite. Only the sender's own active
 * role carries a note; every other tier fills the daily invites with a plain
 * connection request.
 */
function inviteTierTooltip(
	tier: InviteTier | null | undefined,
	assignmentName: string | null | undefined
): string | undefined {
	if (!tier) return undefined;
	const role = assignmentName ? `"${assignmentName}"` : "a role";
	switch (tier) {
		case "assignments":
			return `Invite for your active role ${role}.`;
		case "assignments_same_g":
			return `Voted up on your colleague's active role ${role}. Sent without a note to fill your daily invites.`;
		case "assignments_active":
		case "assignments_egenkonsulter_active":
			return `Voted up on the active role ${role}. Sent without a note to fill your daily invites.`;
		case "assignments_inactive":
		case "assignments_egenkonsulter":
			return `Network invite: voted up on ${role}, which is no longer active. Sent without a note to grow your network for similar roles. No messages follow from an inactive role.`;
	}
}

/**
 * Get chip label and color based on entry type
 */
export function getEntryChipProps(entry: ActivityEntry, theme: Theme): EntryChipProps {
	switch (entry.type) {
		case "email_queued":
			return {
				label: entry.data.assignment_name
					? "Email Pitch Queued"
					: "Email Queued",
				color: "success",
			};
		case "email_sent": {
			// sent=True only means the queue row was processed — a non-null error
			// (e.g. smtp_rejected / probe timeout) means it never actually left.
			if (entry.data.error) {
				return {
					label: "Unable to send Email",
					color: "warning",
					tooltip: entry.data.error,
				};
			}
			if (entry.data.bounced) {
				return {
					label: entry.data.assignment_name
						? "Email Pitch Bounced"
						: "Email Bounced",
					color: "error",
				};
			}
			return {
				label: entry.data.assignment_name ? "Email Pitch Sent" : "Email Sent",
				color: "success",
			};
		}
		case "linkedin_queued":
			return {
				label: entry.data.assignment_name
					? "LinkedIn Pitch Queued"
					: "LinkedIn Queued",
				color: "primary",
			};
		case "linkedin_sent":
			return {
				label: entry.data.assignment_name
					? "LinkedIn Pitch Sent"
					: "LinkedIn Sent",
				color: "primary",
			};
		case "linkedin_conversation":
			return {
				label:
					entry.direction === "inbound"
						? "LinkedIn Reply"
						: "LinkedIn Message",
				color: "primary",
			};
		case "email_journal":
			return {
				label: entry.direction === "inbound" ? "Email Received" : "Email Sent",
				color: "success",
			};
		case "vote":
			return { label: "Vote", color: "secondary" };
		case "event":
			return {
				label: entry.data.name,
				color: "info",
				tooltip: inviteTierTooltip(entry.data.tier, entry.data.assignment_name),
			};
		case "board_movement":
			return {
				label: entry.fromColumn ? "Moved on Board" : "Added to Board",
				customColor: theme.palette.board.main,
			};
		case "note":
			return { label: "Note", color: "warning" };
		case "attachment":
			return { label: "File Uploaded", color: "warning" };
		case "call": {
			const direction =
				entry.data.call_direction === "inbound" ? "Inbound" : "Outbound";
			return { label: `${direction} Call`, color: "info" };
		}
		case "meeting": {
			const meetingType =
				entry.data.meeting_type === "video" ? "Video" : "In-Person";
			return { label: `${meetingType} Meeting`, color: "secondary" };
		}
		case "job_application":
			return { label: "Job Application", color: "success" };
		case "cooldown": {
			const isActive = new Date(entry.data.until) >= new Date();
			return {
				label: isActive ? "Cooldown Set" : "Cooldown Expired",
				color: "info",
			};
		}
	}
}
